import type { NotifiableError } from "@bugsnag/js";
import { notifyBugsnag } from "@circle-react/helpers/bugsnagHelpers";
import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";

export interface ErrorFallbackProps {
  error: Error;
  resetState: () => void;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (props: ErrorFallbackProps) => ReactNode;
  name?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override state: ErrorBoundaryState = { error: null };

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { name, onError } = this.props;
    const label = name ?? "ErrorBoundary";

    if (process.env.NODE_ENV === "development") {
      console.error(`[${label}]`, error, errorInfo);
    }

    notifyBugsnag(error as NotifiableError, {
      errorBoundary: {
        name: label,
        componentStack: errorInfo.componentStack,
      },
    });

    onError?.(error, errorInfo);
  }

  private resetState = () => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;
    if (error) {
      return fallback({ error, resetState: this.resetState });
    }
    return children;
  }
}
